import { alpha, createTheme } from '@mui/material';

import { ColorTokens } from '../types';

import { PERFORMANCE_STATUS } from '@/features/starvationBidder/components/diagnostics/performanceMonitoring/constants';

const primaryColor = {
  50: '#0B5556',
  100: '#257776',
  200: '#3F9996',
  300: '#58BBB5',
  400: '#72DDD5',
  500: '#8CFFF5', // main
  600: '#98FFF6',
  700: '#A3FFF7',
  800: '#AFFFF7',
  900: '#BAFFF8',
  1000: '#C6FFF9',
  customLight: '#171f31',
  customMedium: '#DFE8FC', // not used
};

const secondaryColor = {
  50: '#5F3EAF',
  100: '#6C4BB9',
  200: '#7858C3',
  300: '#8465CD',
  400: '#9173D7',
  500: '#A78BFA', // main
  600: '#B7A1FB',
  700: '#C7B8FC',
  800: '#D7CEFD',
  900: '#E7E4FE',
  1000: '#F4F1FF',
};

export const grey = {
  25: '#838D9E', // darkest
  50: '#8993A2',
  100: '#8F99A7',
  200: '#959FAB',
  300: '#9AA6B0',
  400: '#A0ACB4',
  500: '#A6B2B9',
  600: '#ACB8BD', // main
  700: '#C8D0D3',
  800: '#E3E7E9',
  900: '#FFFFFF', // lightest
};

export const colorTokens: ColorTokens = {
  green: {
    10: '#203026', // soft green-tinted surface
    100: '#1E8F57', // primary green accent
    200: '#2DBB74', // stronger / success emphasis
  },
  orange: {
    50: '#332213', // warm dark surface
    700: '#E05A2A', // vivid orange accent
  },
  yellow: {
    5: '#2A220F', // soft warm dark background
    10: '#342A12', // subtle surface / hover
    100: '#9C6A00', // amber accent
    200: '#B87400', // stronger amber
  },
  red: {
    5: '#3A1C1E', // subtle dark tint for surfaces
    10: '#4A2225', // elevated surface / hover
    100: '#D64541', // standard alert / button / chip
    200: '#F0514F', // strong error / high emphasis
  },
  steel: {
    50: '#1E2A36', // cool blue-gray surface
    700: '#2E90D1', // info / link accent
  },

  // mapbox plot colors
  mapboxBucketColors: {
    map1: ['#D9CCFF', '#A78BFA', '#7C49F0 ', '#5C1EDC'],
    map2: ['#FFD9B8', '#FF8C42', '#E55B00', '#B83A00'],
    map3: ['#FFC2DD', '#F472B6', '#E63A97', '#C31078'],
  },

  // performance monitoring status colors
  performanceStatusColors: {
    [PERFORMANCE_STATUS.EFFICIENT_ON_TRACK]: primaryColor[300],
    [PERFORMANCE_STATUS.EFFICIENT_AT_RISK]: secondaryColor[300],
    [PERFORMANCE_STATUS.INEFFICIENT_ON_TRACK]: '#fed093',
    [PERFORMANCE_STATUS.INEFFICIENT_AT_RISK]: '#fcafa8',
  },

  // bdi/cdi priority group plot colors
  bdiCdiPlotColors: {
    L: {
      stroke: '#2EC4A6', // teal, slightly muted but bright
      fill: '#1F5E54', // dark teal fill
    },
    M: {
      stroke: '#F2C94C', // warm yellow
      fill: '#5A4A14', // dark amber fill
    },
    H: {
      stroke: '#E24A6A', // rose red
      fill: '#5A1E2E', // dark rose fill
    },
    VH: {
      stroke: '#9B5DE5', // violet
      fill: '#3A1E4A', // dark purple fill
    },
  },
};

const plusJakartaSansFontFamily = [
  '"Plus Jakarta Sans"',
  '-apple-system', // macOS / iOS
  'BlinkMacSystemFont', // Chrome Mac
  '"Segoe UI"', // Windows
  'Roboto', // Android / fallback
  'Helvetica',
  'Arial',
  'sans-serif',
].join(',');

const theme = createTheme({
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1272,
      xl: 1536,
    },
  },
  customShadows: {
    xs: `0px 1px 2px 0px ${alpha(grey[900], 0.05)}`,
    sm: `0px 1px 2px 0px ${alpha(grey[900], 0.06)}, 0px 1px 3px 0px ${alpha(grey[900], 0.1)}`,
    md: `0px 2px 4px -2px ${alpha(grey[900], 0.06)}, 0px 4px 8px -2px ${alpha(grey[900], 0.1)}`,
    lg: `0px 4px 6px -2px ${alpha(grey[900], 0.05)}, 0px 12px 16px -4px ${alpha(grey[900], 0.1)}`,
    xl: `0px 8px 8px -4px ${alpha(grey[900], 0.04)}, 0px 20px 24px -4px ${alpha(grey[900], 0.1)}`,
    200: `0px 1px 4px 0px ${alpha(grey[900], 0.05)}, 0px 1px 4px 0px ${alpha(grey[900], 0.1)}`,
    400: `0px 4px 4px -4px ${alpha(grey[900], 0.05)}, 0px 16px 32px -4px ${alpha(grey[900], 0.1)}`,
  },
  palette: {
    mode: 'dark',
    primary: {
      main: primaryColor[500],
      50: primaryColor[50],
      100: primaryColor[100],
      200: primaryColor[200],
      300: primaryColor[300],
      400: primaryColor[400],
      500: primaryColor[500], // main
      600: primaryColor[600],
      700: primaryColor[700], // dark
      800: primaryColor[800],
      900: primaryColor[900],
      1000: primaryColor[1000],
      customLight: primaryColor.customLight,
      customMedium: primaryColor.customMedium,
    },
    secondary: {
      main: secondaryColor[500],
      50: secondaryColor[50],
      100: secondaryColor[100],
      200: secondaryColor[200],
      300: secondaryColor[300],
      400: secondaryColor[400],
      500: secondaryColor[500], // main
      600: secondaryColor[600],
      700: secondaryColor[700],
      800: secondaryColor[800],
      900: secondaryColor[900],
      1000: secondaryColor[1000],
    },
    text: {
      primary: grey[700], // main
      secondary: grey[500], // medium
      tertiary: grey[900], // dark
    },
    grey: {
      25: grey[25], // lightest
      50: grey[50], // light
      100: grey[100],
      200: grey[200],
      300: grey[300],
      400: grey[400],
      500: grey[500], // medium
      600: grey[600],
      700: grey[700], // main
      800: grey[800],
      900: grey[900], // dark
    },
    divider: '#3D5360',
    background: {
      default: '#171f31',
      paper: '#263444', // # quivalent of '#3D536066' without alpha and blend of background '#171f31'
    },
  },

  typography: {
    fontFamily: plusJakartaSansFontFamily,
    h3: {
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: '2.625rem',
      color: grey[900],
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: '2.625rem',
      color: grey[900],
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: '1.875rem',
      color: grey[900],
    },
    body1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: '1.375rem',
    },
    body2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: '1.3125rem',
    },
    body3: {
      fontWeight: 500,
      fontSize: '0.75rem',
      lineHeight: '1.125rem',
    },
    body14: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    body16: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '1.5rem',
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: '1.3125rem',
      textTransform: 'none',
    },
    subtitleNumbers: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    subtitleLabel: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: '1.25rem',
    },
  },

  components: {
    MuiAutocomplete: {
      defaultProps: {
        loadingText: 'Loading..',
        slotProps: {
          listbox: {
            sx: {
              py: 0,
            },
          },
          paper: {
            elevation: 0,
            sx: {
              fontWeight: 400,
              color: 'text.tertiary',
              boxShadow: (theme) => theme.customShadows.xl,
            },
          },
          popper: {
            modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
          },
        },
      },
    },

    MuiDialog: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 1,
          },
        },
      },
    },

    MuiMenu: {
      defaultProps: {
        slotProps: {
          list: {
            sx: {
              py: 0,
            },
          },
        },
      },
    },

    MuiPopover: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 0,
            sx: {
              boxShadow: (theme) => theme.customShadows.xl,
              mt: 0.5,
            },
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        sx: {
          borderRadius: 1,
        },
      },
    },
  },
});

export default theme;
